import type { GencowAuthConfig } from "@gencow/core";

const LOCALHOST_WILDCARD_RE = /^https?:\/\/localhost:\*$/;

export interface OriginPolicy {
  publicOrigins: string[];
  apiAllowedOrigins: string[];
  authTrustedOrigins: string[];
}

export interface OriginPolicyContext {
  baseDomain?: string;
  appPublicDomain?: string;
  capturedBaseURL?: string;
  managedPublicOrigins?: string[] | string;
  frontendOrigins?: string[];
  projectTrustedOrigins?: string[];
  corsOriginsEnv?: string;
  authConfig?: GencowAuthConfig;
}

function normalizeDomain(value?: string): string | null {
  const raw = value?.trim();
  if (!raw) return null;
  return (
    raw
      .replace(/^https?:\/\//, "")
      .split("/")[0]
      ?.trim() || null
  );
}

function splitEnvOrigins(value?: string): string[] {
  return (
    value
      ?.split(",")
      .map((entry) => entry.trim())
      .filter(Boolean) ?? []
  );
}

function isLocalHttpOrigin(url: URL): boolean {
  return (
    url.protocol === "http:" &&
    (url.hostname === "localhost" || url.hostname === "127.0.0.1") &&
    url.port !== ""
  );
}

export function normalizeFrontendOrigin(value: string): string {
  const raw = value.trim();
  if (!raw) throw new Error("Origin is required");
  if (raw.includes("*")) throw new Error("Wildcard origins are not supported");

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    throw new Error("Origin must be a valid URL");
  }

  if (!["http:", "https:"].includes(url.protocol)) {
    throw new Error("Origin must use http:// or https://");
  }
  if (url.protocol === "http:" && !isLocalHttpOrigin(url)) {
    throw new Error("Production frontend origins must use https://");
  }
  if (url.username || url.password) {
    throw new Error("Origin cannot include username or password");
  }
  if (url.pathname !== "/" || url.search || url.hash) {
    throw new Error("Origin must be a bare origin without path, query, or hash");
  }

  return url.origin;
}

function addDomainOrigins(origins: Set<string>, value?: string) {
  const domain = normalizeDomain(value);
  if (!domain || domain.includes("*")) return;

  origins.add(`https://${domain}`);
  origins.add(`http://${domain}`);

  if (domain.startsWith("www.") || domain.includes(":")) {
    return;
  }

  origins.add(`https://www.${domain}`);
  origins.add(`http://www.${domain}`);
}

function hasURLDecoration(url: URL): boolean {
  return (
    url.username !== "" || url.password !== "" || url.pathname !== "/" || url.search !== "" || url.hash !== ""
  );
}

function addExactPublicOrigin(origins: Set<string>, value?: string) {
  if (!value?.trim()) return;
  try {
    origins.add(normalizeFrontendOrigin(value));
  } catch {
    /* Invalid managed/operator origins are ignored by policy resolution. */
  }
}

function addConfiguredOrigin(origins: Set<string>, value?: string) {
  const raw = value?.trim();
  if (!raw) return;

  if (LOCALHOST_WILDCARD_RE.test(raw)) {
    origins.add(raw);
    return;
  }

  if (raw.includes("*")) return;

  try {
    const url = new URL(raw);
    if (!["http:", "https:"].includes(url.protocol) || hasURLDecoration(url)) return;
    origins.add(url.origin);
    return;
  } catch {
    if (raw.includes("/") || raw.includes("?") || raw.includes("#") || raw.includes("@")) return;
    addDomainOrigins(origins, raw);
  }
}

function addURLOrigin(origins: Set<string>, value?: string) {
  const raw = value?.trim();
  if (!raw) return;

  try {
    const url = new URL(raw);
    if (["http:", "https:"].includes(url.protocol) && url.username === "" && url.password === "") {
      origins.add(url.origin);
    }
  } catch {
    addDomainOrigins(origins, raw);
  }
}

function addAuthCallbackOrigins(origins: Set<string>, authConfig?: GencowAuthConfig) {
  addURLOrigin(origins, authConfig?.oauth?.callbackURL);
  for (const callbackURL of authConfig?.oauth?.allowedCallbackURLs ?? []) {
    addURLOrigin(origins, callbackURL);
  }
}

export function resolveOriginPolicy(params: OriginPolicyContext = {}): OriginPolicy {
  const publicOrigins = new Set<string>();

  addDomainOrigins(publicOrigins, params.baseDomain);
  addDomainOrigins(publicOrigins, params.appPublicDomain);
  addURLOrigin(publicOrigins, params.capturedBaseURL);

  const managedPublicOrigins = Array.isArray(params.managedPublicOrigins)
    ? params.managedPublicOrigins
    : splitEnvOrigins(params.managedPublicOrigins);
  for (const origin of managedPublicOrigins) {
    addExactPublicOrigin(publicOrigins, origin);
  }

  for (const origin of params.frontendOrigins ?? []) {
    addExactPublicOrigin(publicOrigins, origin);
  }

  for (const origin of params.projectTrustedOrigins ?? []) {
    addConfiguredOrigin(publicOrigins, origin);
  }
  for (const origin of splitEnvOrigins(params.corsOriginsEnv)) {
    addConfiguredOrigin(publicOrigins, origin);
  }

  const authOrigins = new Set(publicOrigins);
  addAuthCallbackOrigins(authOrigins, params.authConfig);

  return {
    publicOrigins: [...publicOrigins],
    apiAllowedOrigins: [...publicOrigins],
    authTrustedOrigins: [...authOrigins],
  };
}

export function originMatchesAllowedOrigin(origin: string, allowedOrigin: string): boolean {
  if (origin === allowedOrigin) return true;
  if (!LOCALHOST_WILDCARD_RE.test(allowedOrigin)) return false;

  try {
    const url = new URL(origin);
    const allowed = new URL(allowedOrigin.replace(":*", ":1"));
    return url.protocol === allowed.protocol && url.hostname === "localhost";
  } catch {
    return false;
  }
}

export function isOriginAllowed(origin: string, allowedOrigins: string[]): boolean {
  return allowedOrigins.some((allowedOrigin) => originMatchesAllowedOrigin(origin, allowedOrigin));
}

export function resolveCorsOrigin(origin: string | undefined, policy: OriginPolicy): string {
  if (!origin) return "";
  return isOriginAllowed(origin, policy.apiAllowedOrigins) ? origin : "";
}
